import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { projectAPI } from '../api/projectAPI';
import { useAuth } from '../contexts/AuthContext';
import { categoryLabel } from '../i18n/categoryNames';
import './MyProjectsPage.css';

interface Project {
  id: number;
  title: string;
  description: string;
  budget_min: number;
  budget_max: number;
  budget_type: string;
  status: string;
  category_name: string;
  created_at: string;
  updated_at: string;
  views_count: number;
  proposals_count: number;
}

const MyProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  console.log('MyProjectsPage: 렌더링', { user });

  const loadProjects = useCallback(async () => {
    if (!user) {
      console.log('MyProjectsPage: 사용자 정보 없음');
      setLoading(false);
      return;
    }
    try {
      console.log('MyProjectsPage: 프로젝트 목록 로딩 시작', { userId: user.id });
      setLoading(true);
      setError(null);
      const response = await projectAPI.getProjectsByUserId(user.id, user.nickname);
      setProjects(response.projects || []);
    } catch (err: any) {
      console.error('MyProjectsPage: 프로젝트 목록 로딩 실패', err);
      setError(err.response?.data?.message || t('myProjects.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [user, t]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleEdit = (projectId: number) => {
    console.log('MyProjectsPage: 프로젝트 수정 클릭', projectId);
    navigate(`/project-edit/${projectId}`);
  };

  const handleDeleteClick = (projectId: number) => {
    console.log('MyProjectsPage: 삭제 확인 팝업', projectId);
    setDeleteConfirm(projectId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    try {
      console.log('MyProjectsPage: 프로젝트 삭제 시작', deleteConfirm);
      await projectAPI.deleteProject(deleteConfirm);
      console.log('MyProjectsPage: 프로젝트 삭제 성공');
      
      // 목록 새로고침
      setProjects(projects.filter(p => p.id !== deleteConfirm));
      setDeleteConfirm(null);
      alert(t('myProjects.deleted'));
    } catch (err: any) {
      console.error('MyProjectsPage: 프로젝트 삭제 실패', err);
      alert(err.response?.data?.message || t('myProjects.deleteFailed'));
    }
  };

  const handleDeleteCancel = () => {
    console.log('MyProjectsPage: 삭제 취소');
    setDeleteConfirm(null);
  };

  const formatBudget = (min: number, max: number, type: string) => {
    if (type === 'negotiable') return t('projects.negotiable');
    if (min && max) {
      return t('projectDetail.budgetRange', { min: min.toLocaleString(), max: max.toLocaleString() });
    }
    if (min) return t('projectDetail.budgetMin', { v: min.toLocaleString() });
    if (max) return t('projectDetail.budgetMax', { v: max.toLocaleString() });
    return t('projectDetail.tbd');
  };

  const getStatusText = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'open': t('projects.statusInProgress'),
      'in_progress': t('myProjects.statusWorking'),
      'completed': t('transactions.statusCompleted'),
      'closed': t('myProjects.statusClosed'),
    };
    return statusMap[status] || status;
  };

  const getStatusClass = (status: string) => {
    return `status-badge status-${status}`;
  };

  if (loading) {
    return (
      <div className="my-projects-page">
        <div className="container">
          <div className="loading">{t('myProjects.loading')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-projects-page">
        <div className="container">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-projects-page">
      <div className="container">
        <div className="page-header">
          <h1>{t('myProjects.title')}</h1>
          <button 
            className="btn-create"
            onClick={() => navigate('/project-create')}
          >
            + {t('myProjects.newProject')}
          </button>
        </div>

        {projects.length === 0 ? (
          <div className="empty-state">
            <p>{t('myProjects.empty')}</p>
            <button 
              className="btn-create"
              onClick={() => navigate('/project-create')}
            >
              {t('myProjects.firstProject')}
            </button>
          </div>
        ) : (
          <div className="projects-grid">
            {projects.map((project) => (
              <div 
                key={project.id} 
                className="project-card"
                onClick={() => navigate(`/projects/${project.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <div className="card-header">
                  <span className={getStatusClass(project.status)}>
                    {getStatusText(project.status)}
                  </span>
                  <span className="category">{categoryLabel(project.category_name)}</span>
                </div>
                
                <h3 className="project-title">{project.title}</h3>
                
                <p className="project-description">
                  {project.description?.substring(0, 100)}
                  {project.description?.length > 100 && '...'}
                </p>
                
                <div className="project-info">
                  <div className="info-item">
                    <span className="label">{t('projects.budget')}:</span>
                    <span className="value">
                      {formatBudget(project.budget_min, project.budget_max, project.budget_type)}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">{t('myProjects.views')}</span>
                    <span className="value">{project.views_count || 0}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">{t('projects.proposals')}:</span>
                    <span className="value">{t('dashboard.countN', { n: project.proposals_count || 0 })}</span>
                  </div>
                </div>
                
                <div className="card-footer">
                  <span className="created-date">
                    {t('common.createdDate')} {new Date(project.created_at).toLocaleDateString()}
                  </span>
                  <div className="action-buttons">
                    <button 
                      className="btn-view"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/projects/${project.id}`);
                      }}
                    >
                      {t('common.view')}
                    </button>
                    <button 
                      className="btn-edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(project.id);
                      }}
                    >
                      {t('common.edit')}
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(project.id);
                      }}
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 삭제 확인 모달 */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{t('myProjects.deleteTitle')}</h3>
            <p>{t('myProjects.deleteConfirmMsg')}</p>
            <p className="warning">{t('myProjects.deleteWarning')}</p>
            <div className="modal-buttons">
              <button 
                className="btn-cancel"
                onClick={handleDeleteCancel}
              >
                {t('common.cancel')}
              </button>
              <button 
                className="btn-confirm-delete"
                onClick={handleDeleteConfirm}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProjectsPage;
